// Eyes of Madness Effect
// Features: Screen darkens, multiple massive fiery eyes open around the caster,
// and physics-based ribbons representing chaotic knowledge flow into the caster.

const CONFIG = {
    eyeCount: 10,
    ribbonCount: 8,
    scale: 1.2,
    openDuration: 1000,
    stareDuration: 2000,
    fadeDuration: 1000,
    maxDelay: 1900,
    ribbonSpeed: 8,
};

// --- Sprite Caching ---
const spriteCache = new Map<string, HTMLCanvasElement>();

function getMadnessEyeSprite(openProgress: number): HTMLCanvasElement {
    // Quantize progress to reduce cache misses (0 to 1, in steps of 0.05)
    let p = Math.round(openProgress * 20) / 20;
    if (p < 0) p = 0;
    if (p > 1) p = 1;

    const key = `madEye-${p}`;
    let sprite = spriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        const W = 300;
        const H = 200;
        sprite.width = W;
        sprite.height = H;
        const ctx = sprite.getContext('2d');
        if (ctx) {
            const cx = W / 2;
            const cy = H / 2;

            ctx.clearRect(0, 0, W, H);

            // The eye opening height depends on progress
            const eyeH = 60 * p;

            // Outer chaotic glow (Aura)
            if (p > 0) {
                const glowGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, 100 * p + 20);
                glowGrad.addColorStop(0, `rgba(255, 100, 0, ${p * 0.8})`);
                glowGrad.addColorStop(0.5, `rgba(200, 0, 0, ${p * 0.4})`);
                glowGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
                ctx.fillStyle = glowGrad;
                ctx.beginPath();
                ctx.ellipse(cx, cy, 120 * p + 20, 100 * p + 20, 0, 0, Math.PI * 2);
                ctx.fill();
            }

            // The Eyelids / Eye shape
            if (p > 0.05) {
                ctx.save();
                ctx.beginPath();
                // Almond eye shape
                ctx.moveTo(cx - 100, cy);
                ctx.quadraticCurveTo(cx, cy - eyeH * 2, cx + 100, cy);
                ctx.quadraticCurveTo(cx, cy + eyeH * 2, cx - 100, cy);
                ctx.closePath();
                ctx.clip(); // Clip to eye bounds

                // Sclera (white/yellowish inner background) -> Now a smooth glowing gradient with no pupil
                const scleraGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 120);
                scleraGrad.addColorStop(0, '#ffff55'); // Bright, almost white-yellow center
                scleraGrad.addColorStop(0.2, '#ffcc00'); // Core bright yellow
                scleraGrad.addColorStop(0.6, '#ff4400'); // Deep orange
                scleraGrad.addColorStop(1, '#880000'); // Dark red edges
                ctx.fillStyle = scleraGrad;
                ctx.fill();

                // Inner Eye Shadow
                const shadowGrad = ctx.createRadialGradient(cx, cy, 40, cx, cy, 100);
                shadowGrad.addColorStop(0, 'rgba(0,0,0,0)');
                shadowGrad.addColorStop(1, 'rgba(0,0,0,0.8)');
                ctx.fillStyle = shadowGrad;
                ctx.beginPath();
                ctx.moveTo(cx - 100, cy);
                ctx.quadraticCurveTo(cx, cy - eyeH * 2, cx + 100, cy);
                ctx.quadraticCurveTo(cx, cy + eyeH * 2, cx - 100, cy);
                ctx.fill();

                ctx.restore();

                // Eyelid borders (jagged geometry to look like torn reality)
                ctx.strokeStyle = '#cc1100';
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(cx - 100, cy);
                ctx.quadraticCurveTo(cx, cy - eyeH * 2, cx + 100, cy);
                ctx.stroke();

                ctx.strokeStyle = '#660000';
                ctx.lineWidth = 5;
                ctx.beginPath();
                ctx.moveTo(cx - 100, cy);
                ctx.quadraticCurveTo(cx, cy + eyeH * 2, cx + 100, cy);
                ctx.stroke();
            }
        }
        spriteCache.set(key, sprite);
    }
    return sprite;
}

function getSparkSprite(): HTMLCanvasElement {
    const key = 'madSpark';
    let sprite = spriteCache.get(key);
    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = 40;
        sprite.height = 40;
        const ctx = sprite.getContext('2d');
        if (ctx) {
            const grad = ctx.createRadialGradient(20, 20, 0, 20, 20, 20);
            grad.addColorStop(0, 'rgba(255, 255, 100, 1)');
            grad.addColorStop(0.3, 'rgba(255, 100, 0, 0.8)');
            grad.addColorStop(1, 'rgba(255, 0, 0, 0)');
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(20, 20, 20, 0, Math.PI * 2);
            ctx.fill();
        }
        spriteCache.set(key, sprite);
    }
    return sprite;
}

// --- Classes ---

class Point {
    x: number; y: number; oldX: number; oldY: number; pinned: boolean
    constructor(x: number, y: number, pinned = false) {
        this.x = x; this.y = y; this.oldX = x; this.oldY = y; this.pinned = pinned
    }
    update(drag: number) {
        if (this.pinned) return
        const vx = (this.x - this.oldX) * drag
        const vy = (this.y - this.oldY) * drag
        this.oldX = this.x; this.oldY = this.y
        this.x += vx
        this.y += vy
    }
}

class Constraint {
    p1: Point; p2: Point; length: number; stiffness: number
    constructor(p1: Point, p2: Point, length: number, stiffness = 1) {
        this.p1 = p1; this.p2 = p2; this.length = length; this.stiffness = stiffness
    }
    resolve() {
        const dx = this.p1.x - this.p2.x
        const dy = this.p1.y - this.p2.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < 0.001) return
        const diff = (this.length - dist) / dist * this.stiffness
        if (!this.p1.pinned) { this.p1.x += dx * diff * 0.5; this.p1.y += dy * diff * 0.5 }
        if (!this.p2.pinned) { this.p2.x -= dx * diff * 0.5; this.p2.y -= dy * diff * 0.5 }
    }
}

class KnowledgeRibbon {
    points: Point[] = []; constraints: Constraint[] = []
    baseWidth: number;
    colorMain: string;
    targetEntityPos: { x: number, y: number }

    constructor(startX: number, startY: number, segments: number, length: number, width: number, target: { x: number, y: number }) {
        this.baseWidth = width;
        this.targetEntityPos = target;

        const isRed = Math.random() > 0.5;
        this.colorMain = isRed ? `rgba(200, 20, 20, {a})` : `rgba(255, 120, 0, {a})`;

        const segLen = length / segments
        for (let i = 0; i < segments; i++) {
            const p = new Point(startX, startY, i === 0)
            this.points.push(p)
            if (i > 0) this.constraints.push(new Constraint(this.points[i - 1], p, segLen, 0.8))
        }
    }

    update(headX: number, headY: number, targetActive: boolean) {
        // Head follows a path, but if targetActive, the head aggressively seeks the target
        if (targetActive) {
            const dx = this.targetEntityPos.x - headX;
            const dy = this.targetEntityPos.y - headY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 5) {
                headX += (dx / dist) * CONFIG.ribbonSpeed;
                headY += (dy / dist) * CONFIG.ribbonSpeed;
            } else {
                headX = this.targetEntityPos.x;
                headY = this.targetEntityPos.y;
            }
        }

        this.points[0].x = headX
        this.points[0].y = headY

        // Swirling chaos logic for body
        for (let i = 1; i < this.points.length; i++) {
            this.points[i].update(0.85) // high drag for flowy feel

            // Add swirling forces
            if (!targetActive) {
                this.points[i].x += Math.sin((Date.now() * 0.005) + i * 0.2) * 2;
                this.points[i].y += Math.cos((Date.now() * 0.004) + i * 0.2) * 2;
            } else {
                // pulled strongly to the next point
                const dx = this.points[i - 1].x - this.points[i].x;
                const dy = this.points[i - 1].y - this.points[i].y;
                this.points[i].x += dx * 0.1;
                this.points[i].y += dy * 0.1;
            }
        }

        for (let k = 0; k < 3; k++) this.constraints.forEach(c => c.resolve())

        return { x: headX, y: headY };
    }

    draw(ctx: CanvasRenderingContext2D, alpha: number) {
        if (this.points.length < 2) return

        ctx.beginPath()
        const leftSide: { x: number, y: number }[] = []
        const rightSide: { x: number, y: number }[] = []

        for (let i = 0; i < this.points.length; i++) {
            const p = this.points[i]
            let dx = 0, dy = 1
            if (i > 0) { dx = p.x - this.points[i - 1].x; dy = p.y - this.points[i - 1].y }
            else if (i < this.points.length - 1) { dx = this.points[i + 1].x - p.x; dy = this.points[i + 1].y - p.y }

            if (dx === 0 && dy === 0) { dx = 0; dy = 1 }
            let perp = Math.atan2(dy, dx) + Math.PI / 2
            const prog = i / this.points.length // 0 is head, 1 is tail

            let currentWidth = this.baseWidth * (1 - prog); // tapers to tail

            const wx = Math.cos(perp) * currentWidth
            const wy = Math.sin(perp) * currentWidth
            leftSide.push({ x: p.x - wx, y: p.y - wy })
            rightSide.push({ x: p.x + wx, y: p.y + wy })
        }

        ctx.moveTo(leftSide[0].x, leftSide[0].y)
        for (let i = 1; i < leftSide.length; i++) ctx.lineTo(leftSide[i].x, leftSide[i].y)
        for (let i = rightSide.length - 1; i >= 0; i--) ctx.lineTo(rightSide[i].x, rightSide[i].y)
        ctx.closePath()

        ctx.fillStyle = this.colorMain.replace('{a}', alpha.toString());
        ctx.shadowBlur = 10;
        ctx.shadowColor = this.colorMain.replace('{a}', (alpha * 0.5).toString());
        ctx.fill()
        ctx.shadowBlur = 0;
    }
}

class GiantEye {
    x: number; y: number; targetX: number; targetY: number; angle: number;
    scale: number; delay: number;
    ribbons: KnowledgeRibbon[] = [];
    ribbonHeadPos: { x: number, y: number };

    constructor(targetPlayerX: number, targetPlayerY: number, canvasWidth: number, canvasHeight: number, delayMs: number, existingEyes: GiantEye[]) {
        // Spawn randomly around the target, minimum distance check
        const minDistancePadding = 30; // padding between edges
        let spawnDist, spawnAngle, candidateX, candidateY;
        let validPos = false;
        let attempts = 0;

        // Determine scale first to know the radius
        // Bell-curve-ish distribution to favor medium sizes, reducing tiny and giant
        const u = 1 - Math.random(); // Sub uniformly [0,1]
        const v = Math.random();
        const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v); // Box-Muller transform
        // Map from standard normal (mean 0, stddev 1) to desired range.
        // Mean 0.6, stddev 0.25 gives ~95% between 0.1 and 1.1
        let randomScale = 0.6 + z * 0.25;
        if (randomScale < 0.2) randomScale = 0.2;
        if (randomScale > 1.2) randomScale = 1.2;

        this.scale = randomScale * CONFIG.scale;

        // Approximate the collision radius (using 150 since sprite width is 300)
        const myRadius = 150 * this.scale;

        while (!validPos && attempts < 150) {
            // Spawn anywhere on screen within bounds for maximum spread
            candidateX = myRadius + Math.random() * (canvasWidth - myRadius * 2);
            candidateY = myRadius + Math.random() * (canvasHeight - myRadius * 2);

            validPos = true;
            for (const eye of existingEyes) {
                const dx = candidateX - eye.x;
                const dy = candidateY - eye.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                const eyeRadius = 150 * eye.scale;

                // Distance between centers must be greater than sum of radii + padding
                if (dist < myRadius + eyeRadius + minDistancePadding) {
                    validPos = false;
                    break;
                }
            }
            attempts++;
        }

        this.x = candidateX!;
        this.y = candidateY!;
        this.targetX = targetPlayerX;
        this.targetY = targetPlayerY;
        this.delay = delayMs;

        // Angle to look roughly towards the center
        this.angle = Math.atan2(targetPlayerY - this.y, targetPlayerX - this.x) + (Math.random() - 0.5) * 0.5;

        this.ribbonHeadPos = { x: this.x, y: this.y };

        const numRibbons = 1 + Math.floor(Math.random() * 3);
        for (let i = 0; i < numRibbons; i++) {
            this.ribbons.push(new KnowledgeRibbon(this.x, this.y, 15, 100 + Math.random() * 50, 4 + Math.random() * 6, { x: targetPlayerX, y: targetPlayerY }));
        }
    }

    draw(ctx: CanvasRenderingContext2D, t: number, globalAlpha: number) {
        if (t < this.delay) return; // hasn't appeared yet

        const localT = t - this.delay;

        // Open progress: 0 to 1
        let openProgress = 0;
        if (localT < CONFIG.openDuration) {
            openProgress = localT / CONFIG.openDuration;
            // Easing
            openProgress = 1 - Math.pow(1 - openProgress, 3);
        } else if (localT < CONFIG.openDuration + CONFIG.stareDuration) {
            openProgress = 1; // fully open
        } else {
            const fadeT = localT - (CONFIG.openDuration + CONFIG.stareDuration);
            openProgress = 1 - (fadeT / CONFIG.fadeDuration);
            if (openProgress < 0) openProgress = 0;
        }

        if (openProgress <= 0) return;

        ctx.save();
        ctx.globalAlpha = globalAlpha;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.angle);
        ctx.scale(this.scale, this.scale);

        // Pulsing scale effect when fully open
        if (localT > CONFIG.openDuration && localT < CONFIG.openDuration + CONFIG.stareDuration) {
            const pulse = 1 + Math.sin(localT * 0.005) * 0.05;
            ctx.scale(pulse, pulse);
        }

        const sprite = getMadnessEyeSprite(openProgress);
        ctx.drawImage(sprite, -sprite.width / 2, -sprite.height / 2);

        ctx.restore();

        // Ribbons draw
        if (localT > CONFIG.openDuration * 0.5 && localT < CONFIG.openDuration + CONFIG.stareDuration + CONFIG.fadeDuration * 0.5) {
            const isSeeking = localT > CONFIG.openDuration;

            this.ribbons.forEach((r, idx) => {
                // If seeking, head moves to target. Else it stays near eye but wiggles.
                let nextHeadX = this.ribbonHeadPos.x;
                let nextHeadY = this.ribbonHeadPos.y;

                if (!isSeeking) {
                    const wiggleAmt = 30;
                    nextHeadX = this.x + Math.cos(localT * 0.002 + idx) * wiggleAmt;
                    nextHeadY = this.y + Math.sin(localT * 0.003 + idx) * wiggleAmt;
                }

                this.ribbonHeadPos = r.update(nextHeadX, nextHeadY, isSeeking);

                let rAlpha = (localT > CONFIG.openDuration + CONFIG.stareDuration)
                    ? Math.max(0, 1 - (localT - CONFIG.openDuration - CONFIG.stareDuration) / (CONFIG.fadeDuration * 0.5))
                    : 1;

                r.draw(ctx, rAlpha * globalAlpha);
            });
        }
    }
}

class Spark {
    x: number; y: number; vx: number; vy: number; life: number; maxLife: number; size: number;
    constructor(startX: number, startY: number) {
        this.x = startX; this.y = startY;
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 5;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.maxLife = 500 + Math.random() * 500;
        this.life = this.maxLife;
        this.size = 0.5 + Math.random() * 1.5;
    }
    update(dt: number) {
        this.x += this.vx * (dt / 16);
        this.y += this.vy * (dt / 16);
        this.vx *= 0.95; // friction
        this.vy *= 0.95;
        this.life -= dt;
    }
    draw(ctx: CanvasRenderingContext2D, alphaMultiplier: number) {
        if (this.life <= 0) return;
        const a = (this.life / this.maxLife) * alphaMultiplier;
        ctx.globalAlpha = a;
        const sprite = getSparkSprite();
        ctx.drawImage(sprite, this.x - sprite.width / 2 * this.size, this.y - sprite.height / 2 * this.size, sprite.width * this.size, sprite.height * this.size);
    }
}


export type EyesOfMadnessEffect = {
    kind: 'eyesOfMadness'
    t: number
    cx: number  // Caster X
    cy: number  // Caster Y
    data?: {
        eyes: GiantEye[]
        sparks: Spark[]
        lastTime: number
    }
}

export const createEyesOfMadnessEffect = (cx: number, cy: number): EyesOfMadnessEffect => ({
    kind: 'eyesOfMadness', t: 0, cx, cy
})

export const renderEyesOfMadnessEffect = (
    eff: EyesOfMadnessEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    const { width, height } = ctx.canvas;

    if (!eff.data) {
        const eyes: GiantEye[] = [];
        for (let i = 0; i < CONFIG.eyeCount; i++) {
            // Stagger appearances
            eyes.push(new GiantEye(eff.cx, eff.cy, width, height, Math.random() * CONFIG.maxDelay, eyes));
        }

        eff.data = {
            eyes,
            sparks: [],
            lastTime: performance.now()
        };
    }

    const d = eff.data;
    const now = performance.now();
    const dt = now - d.lastTime;
    d.lastTime = now;

    const totalDuration = CONFIG.openDuration + CONFIG.stareDuration + CONFIG.fadeDuration + CONFIG.maxDelay + 100;

    if (eff.t > totalDuration) {
        return false;
    }

    // Vignette background darkening
    let bgAlpha = 0;
    if (eff.t < 1000) bgAlpha = eff.t / 1000 * 0.7;
    else if (eff.t < totalDuration - 1000) bgAlpha = 0.7;
    else bgAlpha = Math.max(0, (totalDuration - eff.t) / 1000 * 0.7);

    ctx.save();

    if (bgAlpha > 0.01) {
        // Render ominous background
        const bgGrad = ctx.createRadialGradient(eff.cx, eff.cy, 50, eff.cx, eff.cy, Math.max(width, height) * 0.8);
        bgGrad.addColorStop(0, `rgba(50, 0, 0, ${bgAlpha * 0.5})`);
        bgGrad.addColorStop(1, `rgba(0, 0, 0, ${bgAlpha})`);
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);
    }

    ctx.globalCompositeOperation = 'screen';

    // Draw eyes and update ribbons
    d.eyes.forEach(eye => {
        eye.draw(ctx, eff.t, 1.0);

        // Spawn sparks if staring
        if (eff.t > eye.delay + CONFIG.openDuration && eff.t < eye.delay + CONFIG.openDuration + CONFIG.stareDuration) {
            if (Math.random() < 0.2) {
                d.sparks.push(new Spark(eye.x, eye.y));
            }
        }
    });

    // Update and draw sparks
    for (let i = d.sparks.length - 1; i >= 0; i--) {
        const s = d.sparks[i];
        s.update(dt);
        s.draw(ctx, 1.0);
        if (s.life <= 0) {
            d.sparks[i] = d.sparks[d.sparks.length - 1];
            d.sparks.pop();
        }
    }

    ctx.restore();

    return true;
}
